
/*!
 * Copyright (C) 2022-2025, Peppermill Inc.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *//*
 *		2022-08-16
 *			- Refactor filter-widget.js hotel room widget related functionality to <hotel-room-details> component.
 *
 *		2023-04-03 (jhoward)
 *			- Refactor <hotel-room-details> to use TypeScript.
 *			- Show rate details in <hotel-room-details>.
 *
 *		2023-06-20 (jhoward)
 *			- Fix mobile calendar layout in <hotel-room-details>.
 *
 *		2024-08-30 (jhoward)
 *			- Remove navigation arrows for the calendar in <hotel-room-details> as they are now included in the calendar component.
 *
 *		2025-02-13 (jhoward)
 *			- Show multiple types of beds if available in <hotel-room-details>.
*/

import {ComponentPublicInstance, defineComponent, PropType} from "vue";
import HotelRoomCallToAction from "./hotelRoomCallToAction.vue";
import HotelCalendar from "./hotelCalendar.vue";
import HotelRoomIcons from "./hotelRoomIcons.vue";
import {
  FilterCache,
  HotelPage,
  HotelRoomData,
  PageListRoomSearchData,
  ReservationDataCache,
  RoomAvailabilityData
} from "./defs";
import KeyUpEvent = JQuery.KeyUpEvent;

export default defineComponent({
  name: 'hotelRoomDetails',
  components: {
    HotelCalendar,
    HotelRoomCallToAction,
    HotelRoomIcons,
  },
  props: {
    offerCodes: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    searchData: {
      type: Object as PropType<PageListRoomSearchData>,
      default: () => {
        return {
          'SQ-FT-LOW': 0,
          'SQ-FT-HIGH': 0,
          'BED': '',
          'BATH-COUNT': 0
        };
      }
    },
    minCheckInDate: {
      type: String,
      required: true
    },
    minCheckOutDate: {
      type: String,
      required: true
    },
    page: {
      type: Object as PropType<HotelPage>,
      default: () => {
        return {
          ID: 0,
          TITLE: '',
          URL: '',
          IMG_SRC: '',
          IMG_FOCUS_X: 50,
          IMG_FOCUS_Y: 50
        };
      }
    },
    roomFloorPlanUrl: {
      type: String,
      default: ''
    },
    roomData: {
      type: Array as PropType<HotelRoomData[]>,
      default: () => {
        return [{
          ROOM_TITLE: '',
          ROOM_TITLE_FIXED: '',
          ROOM_SQ_FT: 0,
          ROOM_BED_TYPE: '',
          ROOM_BED_COUNT: 0,
          ROOM_BATH_COUNT: 0,
          ROOM_SMOKING: false,
          ROOM_NON_SMOKING: false,
          ROOM_ADA: false,
          BOOKING_LINK: '#'
        }];
      }
    }
  },
  mounted() {
    $(this.initialize);
  },
  data() {
    return {
      isInitialized: false,

      savedResData: {} as ReservationDataCache,

      filterCacheLabel: 'hotel-filter-widget-selections',
      savedFilterData: {} as FilterCache,

      offerCode: '',
      iata: '',

      calendarComponent: '',
      calendarCurrentMonthIndex: -1,
      calendarMonthNodes: [],

      currentUiBreakpoint: 'mobile',

      calendarOpen: false,
      calendarLoaded: false,
    };
  },
  computed: {
    checkInDateFormatted() {
      if (this.$store.state.checkInDate) {
        return this.reservationWidgetPrepareDate(this.$store.state.checkInDate);
      }

      return '';
    },
    checkOutDateFormatted() {
      if (this.$store.state.checkOutDate) {
        return this.reservationWidgetPrepareDate(this.$store.state.checkOutDate);
      }

      return '';
    },

    calendarNumberOfMonthsToShow() {
      if ('mobile' === this.currentUiBreakpoint) {
        return 1;
      }

      return 2;
    },

    roomSqFt(): string {
      if (this.searchData['SQ-FT-HIGH'] !== this.searchData['SQ-FT-LOW']) {
        return this.searchData['SQ-FT-LOW'] + '-' + this.searchData['SQ-FT-HIGH'];
      }

      return '' + this.searchData['SQ-FT-HIGH'];
    },
    bedTypes(): {[key: string]: number[]} {
      const output: {[key: string]: number[]} = {};
      this.roomData.forEach(roomTypeData => {
        if (roomTypeData.ROOM_BED_TYPE.length && roomTypeData.ROOM_BED_COUNT > 0) {
          if (!output.hasOwnProperty(roomTypeData.ROOM_BED_TYPE)) {
            output[roomTypeData.ROOM_BED_TYPE] = [];
          }
          if (-1 === output[roomTypeData.ROOM_BED_TYPE].indexOf(roomTypeData.ROOM_BED_COUNT)) {
            output[roomTypeData.ROOM_BED_TYPE].push(roomTypeData.ROOM_BED_COUNT);
          }
        }
      });

      return output;
    },
    bedCount(): string {
      const output: string[] = [];
      for (const bedType in this.bedTypes) {
        output.push(this.bedTypes[bedType].join(' or '));
      }

      return output.join(' / ');
      // return this.searchData['BED'].split('-')[0];
    },
    bedType(): string {
      const output: string[] = [];
      for (const bedType in this.bedTypes) {
        output.push(bedType);
      }

      return output.join(' / ');
      // return this.searchData['BED'].split('-')[1] || '';
    },

    calendarWrapperClasses() {
      return {
        hide: !this.calendarOpen
      };
    },

    roomAvailabilityData(): RoomAvailabilityData {
      if (this.page.ID > 0) {
        for (let i = 0, l = this.$store.state.ROOMS.length; i < l; i++) {
          if (parseInt(this.$store.state.ROOMS[i].PAGE_ID, 10) === this.page.ID) {
            return this.$store.state.ROOMS[i];
          }
        }
      }
      return {
        ADA_VARIANTS: [],
        DATA_AVAILABLE: false,
        PAGE_ID: 0,
        ROOM_TYPES: []
      };
    },
  },
  watch: {
    savedFilterData() {
      //HANDLE CHECK IN/OUT DATES
      this.setCheckInAndCheckOutDates();
    },
    offerCode() {
      this.saveOfferCodeForLater(1500);
    },

    calendarLoaded() {
      if (this.calendarLoaded) {
        requestAnimationFrame(() => {
          const calendarNode: Element | undefined = (this.$refs.calendar as ComponentPublicInstance | null)?.$el;
          if (calendarNode) {
            this.calendarMonthNodes = Array.from(calendarNode.querySelectorAll('.rate-calendar'));
            const firstMonthShownNode: HTMLElement | null = calendarNode.querySelector('.rate-calendar:not(.hide)');
            if (firstMonthShownNode) {
              this.calendarCurrentMonthIndex = this.calendarMonthNodes.indexOf(firstMonthShownNode as never);
            }
          }
        });
      }
    },

    calendarCurrentMonthIndex() {
      this.calendarMonthNodes.forEach((rateCalendarNode: HTMLElement, rateCalendarIndex: number) => {
        if (rateCalendarIndex === this.calendarCurrentMonthIndex || rateCalendarIndex === this.calendarCurrentMonthIndex + this.calendarNumberOfMonthsToShow - 1) {
          rateCalendarNode.classList.remove('hide');
        } else {
          rateCalendarNode.classList.add('hide');
        }
      });
    },
    checkInDate() {
      if (this.$store.state.checkInDate && this.$store.state.checkOutDate) {
        window.hotelRoomsVueApp.saveReservationSelections($(this.$el).data('initialized', this.isInitialized), this.$store.state.checkInDate, this.$store.state.checkOutDate);
      }
    },
    checkOutDate() {
      if (this.$store.state.checkInDate && this.$store.state.checkOutDate) {
        window.hotelRoomsVueApp.saveReservationSelections($(this.$el).data('initialized', this.isInitialized), this.$store.state.checkInDate, this.$store.state.checkOutDate);
      }
    }
  },
  methods: {
    initialize() {
      document.addEventListener('click', () => {
        this.calendarOpen = false;
      }, {passive: true});
      window.addEventListener('resize', () => {
        requestAnimationFrame(this.setCurrentUiBreakpoint);
      }, {
        passive: true
      });
      requestAnimationFrame(this.setCurrentUiBreakpoint);

      if (window.hasOwnProperty('pepvalidateInitForm')) {
        window.pepvalidateInitForm.call(this.$refs.form as HTMLFormElement);
      }

      this.getSavedReservationData();

      this.getSavedFilterData();

      this.setCheckInAndCheckOutDates();

      this.isInitialized = true;
    },
    setCurrentUiBreakpoint(): void {
      const htmlClassList = document.querySelector('html')?.classList;
      if (htmlClassList) {
        if (htmlClassList.contains('ui-mobile')) {
          this.currentUiBreakpoint = 'mobile';
        } else if (htmlClassList.contains('ui-tablet')) {
          this.currentUiBreakpoint = 'tablet';
        } else if (htmlClassList.contains('ui-desktop')) {
          this.currentUiBreakpoint = 'desktop';
        } else if (htmlClassList.contains('ui-widescreen')) {
          this.currentUiBreakpoint = 'widescreen';
        }
      }
    },
    setCheckInAndCheckOutDates(): void {
      const startQuerystringDate = window.hotelRoomsVueApp.formatYYYYMMDDToDateObject(window.pepHelperFunctions.getParameterByName('start'));
      const endQuerystringDate = window.hotelRoomsVueApp.formatYYYYMMDDToDateObject(window.pepHelperFunctions.getParameterByName('end'));

      if (this.savedResData.checkin) {
        this.$store.commit("updateCheckInDate", new Date(this.savedResData.checkin));

        if (this.savedResData.checkout) {
          this.$store.commit('updateCheckOutDate', new Date(this.savedResData.checkout));
        } else {
          this.$store.commit('updateCheckOutDate', window.hotelRoomsVueApp.addDays(this.$store.state.checkInDate, 2));
        }
      } else if (startQuerystringDate && endQuerystringDate) {
        this.$store.commit("updateCheckInDate", startQuerystringDate);
        this.$store.commit("updateCheckOutDate", endQuerystringDate);
      }
    },
    getSavedReservationData(): void {
      this.savedResData = window.hotelRoomsVueApp.getReservationSelections();
      if (this.savedResData.code) {
        this.offerCode = this.savedResData.code;
      }
      if (this.savedResData.iata) {
        this.iata = this.savedResData.iata;
      }
    },
    getSavedFilterData() {
      const cachedFilterData = window.pepHelperFunctions.getCachedData(this.filterCacheLabel, 1800000);
      if (!!cachedFilterData && cachedFilterData.DATA?.filters) {
        const filterCache: FilterCache | null = cachedFilterData.DATA.filters;
        if (filterCache) {
          for (let key in filterCache) {
            filterCache[key as keyof FilterCache]?.forEach((value: string) => {
              this.$store.commit('updateFilter', {
                key,
                value,
                selected: true
              });
            });
          }
        }
      }
    },


    reservationWidgetPrepareDate(date: Date | string | null) {
      //make sure a date was passed in
      if (typeof date !== 'object' || !(date instanceof Date)) {
        return '';
      }

      //var monthNames	=	($container.hasClass('hotel-page'))	?	window.pepDateFunctions.language.en.monthsShort	:	window.pepDateFunctions.language.en.months;
      const newDay = date.getDate().toString(10);
      const newSuffix = (newDay === '11' || newDay === '12' || newDay === '13')
        ? 'th'
        : (newDay.substr(-1, 1) === '1')
          ? 'st'
          : (newDay.substr(-1, 1) === '2')
            ? 'nd'
            : (newDay.substr(-1, 1) === '3')
              ? 'rd'
              : 'th';

      return window.pepDateFunctions.language.en.daysShort[date.getDay()] + ', ' + window.pepDateFunctions.language.en.monthsShort[date.getMonth()] + ' ' + newDay + newSuffix;
    },


    showCalendars(startOnCheckOut?: boolean) {
      if (!this.calendarOpen) {
        if ('boolean' !== typeof startOnCheckOut) {
          startOnCheckOut = false;
        }

        if ('' === this.calendarComponent) {
          this.calendarComponent = 'hotel-calendar';
        }

        this.calendarOpen = true;
      }
    },
    setDatesFromCalendar(dayCheckIn: Date | null, dayCheckOut: Date | null, initialCheckIn: Date | null, initialCheckOut: Date | null) {
      if (
        dayCheckIn
        && dayCheckOut
        && (
          dayCheckIn.getTime() !== initialCheckIn?.getTime()
          || dayCheckOut.getTime() !== initialCheckOut?.getTime()
        )
      ) {
        document.querySelector('.page-link-filter .pepvalidate-v3')?.dispatchEvent(new CustomEvent('submit', {detail: 'skipScroll'}));
      }
    },

    offerCodeKeyUpHandler(event: KeyboardEvent) {
      if (event.key === 'Enter') {
        const buttonNodes = this.$el.querySelectorAll('.non-resort-page, .filter-submit input');
        for (let i = 0, l = buttonNodes.length; i < l; i++) {
          if ('visible' === getComputedStyle(buttonNodes[i]).visibility) {
            buttonNodes[i].dispatchEvent(new Event('click'));
            break;
          }
        }
      }
    },
    saveOfferCodeForLater(timeout?: number) {
      window.hotelRoomsVueApp.saveReservationSelections($(this.$el).data('initialized', this.isInitialized), this.$store.state.checkInDate, this.$store.state.checkOutDate);
    },
  }
})
